import { useState } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { 
  Building2, 
  CreditCard, 
  FileText, 
  Calculator, 
  Mail, 
  Shield,
  Palette,
  Database,
  Settings as SettingsIcon,
  Download,
  Upload,
  RotateCcw,
  Circle
} from 'lucide-react';

// Settings components
import CompanySettings from './settings/CompanySettings';
import BankingSettings from './settings/BankingSettings';
import InvoiceSettings from './settings/InvoiceSettings';
import TaxSettings from './settings/TaxSettings';
import EmailSettings from './settings/EmailSettings';
import SecuritySettings from './settings/SecuritySettings';
import AppearanceSettings from './settings/AppearanceSettings';
import DataSettings from './settings/DataSettings';
import PaymentProvidersSettings from './settings/PaymentProvidersSettings';
import PermissionsSettings from './settings/PermissionsSettings';
import { useConfirmation } from '../components/ConfirmationModal';

const SettingsPage = ({ settings, userPermissions, onSave }) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const { showConfirmation, ConfirmationComponent } = useConfirmation();

  const settingsMenu = [
    {
      id: 'company',
      label: 'Company Info',
      icon: <Building2 size={18} />,
      path: '/admin/settings/company',
      description: 'Company details, address, and contact information'
    },
    {
      id: 'banking',
      label: 'Banking & Payment',
      icon: <CreditCard size={18} />,
      path: '/admin/settings/banking',
      description: 'Bank details and payment settings'
    },
    {
      id: 'invoices',
      label: 'Invoice & Quote',
      icon: <FileText size={18} />,
      path: '/admin/settings/invoices',
      description: 'Numbering, prefixes, and document settings'
    },
    {
      id: 'tax',
      label: 'Tax Settings',
      icon: <Calculator size={18} />,
      path: '/admin/settings/tax',
      description: 'VAT, tax rates, and exemptions'
    },
    {
      id: 'email',
      label: 'Email Settings',
      icon: <Mail size={18} />,
      path: '/admin/settings/email',
      description: 'Email and SMTP settings'
    },
    {
      id: 'payment-providers',
      label: 'Payment Providers',
      icon: <CreditCard size={18} />,
      path: '/admin/settings/payment-providers',
      description: 'Configure payment gateways'
    },
    {
      id: 'permissions',
      label: 'User Permissions',
      icon: <Shield size={18} />,
      path: '/admin/settings/permissions',
      description: 'Manage user roles and permissions'
    },
    // {
    //   id: 'appearance',
    //   label: 'Appearance',
    //   icon: <Palette size={18} />,
    //   path: '/settings/appearance',
    //   description: 'Themes, colors, and branding'
    // },
    // {
    //   id: 'security',
    //   label: 'Security',
    //   icon: <Shield size={18} />,
    //   path: '/settings/security',
    //   description: 'Password, backup, and security settings'
    // },
    // {
    //   id: 'data',
    //   label: 'Data Management',
    //   icon: <Database size={18} />,
    //   path: '/settings/data',
    //   description: 'Import, export, and backup data'
    // }
  ];

  const handleSettingsChange = (newSettings) => {
    onSave(newSettings);
    setHasUnsavedChanges(false);
  };

  const handleFormChange = () => {
    setHasUnsavedChanges(true);
  };

  const exportSettings = async () => {
    setExportProgress(0);
    
    // Simulate export progress
    for (let i = 0; i <= 100; i += 10) {
      await new Promise(resolve => setTimeout(resolve, 100));
      setExportProgress(i);
    }
    
    // Create and download the settings file
    const settingsData = {
      exportDate: new Date().toISOString(),
      version: '1.0',
      settings: settings
    };
    
    const blob = new Blob([JSON.stringify(settingsData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `settings-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    setExportProgress(null);
  };

  const importSettings = (file) => {
    if (!file) return;
    
    setImportProgress(0);
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        for (let i = 0; i <= 100; i += 20) {
          await new Promise(resolve => setTimeout(resolve, 100));
          setImportProgress(i);
        }
        
        const importedData = JSON.parse(e.target.result);
        
        if (importedData.settings) {
          const confirmed = await showConfirmation({
            title: 'Import Settings',
            message: 'Import these settings? This will overwrite your current settings.',
            confirmText: 'Import Settings',
            variant: 'warning'
          });
          
          if (confirmed) {
            handleSettingsChange(importedData.settings);
            alert('Settings imported successfully!');
          }
        } else {
          alert('Invalid settings file format.');
        }
      } catch (error) {
        alert('Error reading settings file: ' + error.message);
      }
      
      setImportProgress(null);
    };
    
    reader.readAsText(file);
  };

  const resetToDefaults = async () => {
    const confirmed = await showConfirmation({
      title: 'Reset Settings',
      message: 'Reset all settings to defaults? This action cannot be undone.',
      confirmText: 'Reset to Defaults',
      variant: 'danger'
    });
    
    if (confirmed) {
      const defaultSettings = {
        // Company defaults
        name: 'Your Company Name',
        address: '',
        phone: '',
        email: '',
        website: '',
        companyNumber: '',
        vatNumber: '',
        logo: '',
        
        // Invoice defaults
        currency: '£',
        invoicePrefix: 'INV',
        quotePrefix: 'QTE',
        invoiceStartNumber: 1,
        quoteStartNumber: 1,
        useYearInNumbers: false,
        
        // Tax defaults
        taxPercentage: 20,
        taxExempt: false,
        taxName: 'VAT',
        taxNumber: '',
        
        // Banking defaults
        bankName: '',
        bankAddress: '',
        sortCode: '',
        accountNumber: '',
        iban: '',
        bic: '',
        defaultPaymentTerms: 'Net 30',
        paymentMethods: '',
        paymentInstructions: '',
        
        // Email defaults
        smtpHost: '',
        smtpPort: 587,
        smtpUsername: '',
        smtpPassword: '',
        smtpEncryption: 'tls',
        fromEmail: '',
        fromName: '',
        replyToEmail: '',
        emailSignature: '',
        autoSendInvoices: false,
        autoSendQuotes: false,
        autoSendReminders: false,
        reminderDaysBefore: 7,
        reminderDaysAfter: [1, 7, 14],
        
        // Security defaults
        requireStrongPasswords: true,
        passwordMinLength: 8,
        passwordRequireUppercase: true,
        passwordRequireLowercase: true,
        passwordRequireNumbers: true,
        passwordRequireSymbols: false,
        sessionTimeout: 60,
        maxLoginAttempts: 5,
        lockoutDuration: 15,
        twoFactorEnabled: false,
        backupEnabled: true,
        backupFrequency: 'daily',
        backupRetention: 30,
        backupLocation: 'local',
        auditLogging: true,
        logRetention: 90,
        ipWhitelist: '',
        allowRemoteAccess: true,
        
        // Appearance defaults
        theme: 'light',
        primaryColor: '#007bff',
        secondaryColor: '#6c757d',
        successColor: '#28a745',
        warningColor: '#ffc107',
        dangerColor: '#dc3545',
        fontFamily: 'system',
        fontSize: 'medium',
        compactMode: false,
        showBrandingOnDocuments: true,
        customCSS: '',
        logoPosition: 'top-left',
        logoSize: 'medium',
        documentTemplate: 'modern',
        brandColors: {
          primary: '#007bff',
          secondary: '#6c757d'
        },
        
        // Data defaults
        autoArchiveInvoices: true,
        archiveAfterDays: 365,
        autoDeleteDrafts: false,
        deleteDraftsAfterDays: 30,
        dataRetentionPeriod: 2555,
        exportFormat: 'json',
        includeDocuments: true,
        includeImages: false,
        compressionLevel: 'medium'
      };
      
      handleSettingsChange(defaultSettings);
    }
  };

  return (
    <>
      <ConfirmationComponent />
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h5 className="flex items-center text-lg font-semibold text-gray-800">
                <SettingsIcon size={20} className="mr-2" />
                Settings
              </h5>
              {hasUnsavedChanges && (
                <div className="flex items-center mt-2 text-sm text-yellow-600">
                  <Circle size={8} className="mr-1 fill-current" />
                  Unsaved changes
                </div>
              )}
            </div>
            <div className="py-2">
              {settingsMenu.map(item => (
                <NavLink
                  key={item.id}
                  to={item.path}
                  className={({ isActive }) => 
                    `block px-6 py-3 border-0 transition-colors duration-150 ${
                      isActive 
                        ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-500' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`
                  }
                >
                  <div className="flex items-start">
                    <div className="mr-3 mt-1 flex-shrink-0">
                      {item.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{item.label}</div>
                      <div className="text-sm text-gray-500 mt-1">{item.description}</div>
                    </div>
                  </div>
                </NavLink>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h6 className="text-lg font-semibold text-gray-800">Quick Actions</h6>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <p className="text-sm text-gray-600">Does not include Payment Providers settings.</p>
                
                <button 
                  className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors disabled:opacity-50"
                  onClick={exportSettings}
                  disabled={exportProgress !== null}
                >
                  <Download size={16} className="mr-2" />
                  {exportProgress !== null ? `Exporting ${exportProgress}%` : 'Export Settings'}
                </button>
                
                <label className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors cursor-pointer">
                  <Upload size={16} className="mr-2" />
                  Import Settings
                  <input
                    type="file"
                    accept=".json"
                    onChange={(e) => importSettings(e.target.files[0])}
                    className="hidden"
                    disabled={importProgress !== null}
                  />
                </label>
                
                {importProgress !== null && (
                  <div className="w-full bg-gray-200 rounded-full h-5 overflow-hidden">
                    <div 
                      className="bg-blue-600 h-full flex items-center justify-center text-xs font-medium text-white transition-all duration-300"
                      style={{ width: `${importProgress}%` }}
                    >
                      {importProgress}%
                    </div>
                  </div>
                )}
                
                <button 
                  className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-md hover:bg-yellow-100 transition-colors"
                  onClick={resetToDefaults}
                >
                  <RotateCcw size={16} className="mr-2" />
                  Reset to Defaults
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <Routes>
            <Route path="/" element={<Navigate to="/admin/settings/company" replace />} />
            <Route 
              path="/company" 
              element={
                <CompanySettings 
                  settings={settings} 
                  onSave={handleSettingsChange}
                  onChange={handleFormChange}
                />
              } 
            />
            <Route 
              path="/banking" 
              element={
                <BankingSettings 
                  settings={settings} 
                  onSave={handleSettingsChange}
                  onChange={handleFormChange}
                />
              } 
            />
            <Route 
              path="/invoices" 
              element={
                <InvoiceSettings 
                  settings={settings} 
                  onSave={handleSettingsChange}
                  onChange={handleFormChange}
                />
              } 
            />
            <Route 
              path="/tax" 
              element={
                <TaxSettings 
                  settings={settings} 
                  onSave={handleSettingsChange}
                  onChange={handleFormChange}
                />
              } 
            />
            <Route 
              path="/email" 
              element={
                <EmailSettings 
                  settings={settings} 
                  onSave={handleSettingsChange}
                  onChange={handleFormChange}
                />
              } 
            />
            <Route
              path="/payment-providers"
              element={
                <PaymentProvidersSettings />
              }
            />
            <Route
              path="/permissions"
              element={
                <PermissionsSettings
                  settings={userPermissions}
                  onSave={handleSettingsChange}
                  onChange={handleFormChange}
                />
              }
            />
            <Route
              path="/appearance"
              element={
                <AppearanceSettings
                  settings={settings}
                  onSave={handleSettingsChange}
                  onChange={handleFormChange}
                />
              }
            />
            <Route 
              path="/security" 
              element={
                <SecuritySettings 
                  settings={settings} 
                  onSave={handleSettingsChange}
                  onChange={handleFormChange}
                />
              } 
            />
            <Route 
              path="/data" 
              element={
                <DataSettings 
                  settings={settings} 
                  onSave={handleSettingsChange}
                  onChange={handleFormChange}
                />
              } 
            />
          </Routes>
        </div>
      </div>
    </>
  );
};

export default SettingsPage;